// src/diagrams/contactFlowReactFlowParser.js - Convert Amazon Connect flows to React Flow format with edge routing

// Action types that do NOT emit an implicit "success" (NextAction) transition.
// Single source of truth shared by transition building (parser) and handle
// validation (ContactFlowDiagram.nodeHasRequiredHandle) to prevent drift.
export const NO_SUCCESS_TRANSITION_TYPES = [
  'CheckHoursOfOperation',
  'CheckMetricData',
  'Compare',
  'GetParticipantInput',
  'ConnectParticipantWithLexBot',
  'DistributeByPercentage',
  'Loop',
  'TransferContactToQueue',
  'ShowView',
  'Wait'
];

export class ContactFlowReactFlowParser {
  constructor(flowContent, exportedData = null, instanceId = null, layoutDirection = 'TB') {
    this.flow = typeof flowContent === 'string' ? JSON.parse(flowContent) : flowContent;
    this.actions = new Map();
    this.metadata = this.flow.Metadata?.ActionMetadata || {};
    this.startAction = this.flow.StartAction;
    this.exportedData = exportedData;
    this.instanceId = instanceId;
    this.layoutDirection = layoutDirection;
    
    // Build actions map for easier lookup
    if (this.flow.Actions) {
      this.flow.Actions.forEach(action => {
        this.actions.set(action.Identifier, action);
      });
    }
  }

  // Convert Contact Flow to React Flow format
  convertToReactFlow() {
    const actions = this.getActions();
    const transitions = this.getAllTransitions();

    // Create nodes (including entry node)
    const nodes = this.createNodesWithEntry(actions);
    
    // Create edges with routing to prevent overlaps
    const edges = this.createEdgesWithRouting(transitions, nodes);

    return { nodes, edges };
  }

  // Create edges with routing logic to prevent overlaps
  createEdgesWithRouting(transitions, nodes) {
    // Group transitions by source-target pairs
    const edgeGroups = new Map();
    
    transitions.forEach((transition, index) => {
      const key = `${transition.from}-${transition.to}`;
      if (!edgeGroups.has(key)) {
        edgeGroups.set(key, []);
      }
      edgeGroups.get(key).push({ ...transition, originalIndex: index });
    });

    const edges = [];
    
    // Process each group and add routing information
    edgeGroups.forEach((groupTransitions, edgeKey) => {
      const totalEdges = groupTransitions.length;
      
      groupTransitions.forEach((transition, groupIndex) => {
        const edge = this.createEdge(transition, transition.originalIndex, nodes);
        
        // Add routing information for parallel edges
        if (totalEdges > 1) {
          edge.data = {
            ...edge.data,
            isParallel: true,
            parallelIndex: groupIndex,
            totalParallel: totalEdges,
            routingOffset: this.calculateRoutingOffset(groupIndex, totalEdges)
          };
        }
        
        // Add layout direction for obstacle avoidance (all edges get this)
        edge.data = {
          ...edge.data,
          layoutDirection: this.layoutDirection,
          // Note: nodes will be added when the edges are processed in the diagram component
        };
        
        edges.push(edge);
      });
    });

    return edges;
  }

  // Calculate routing offset for parallel edges
  calculateRoutingOffset(index, total) {
    if (total === 1) return 0;
    
    // For multiple edges, spread them out
    const spacing = 40; // pixels between parallel edges
    const center = (total - 1) / 2;
    return (index - center) * spacing;
  }

  // Create nodes including an entry node
  createNodesWithEntry(actions) {
    const nodes = [];
    
    // Create entry node
    const entryNode = {
      id: 'entry-node',
      type: 'entryNode',
      position: { x: 0, y: 0 }, // Will be positioned by layout
      data: {
        label: 'Entry',
        actionType: 'Entry',
        category: 'system',
        parameters: {},
        isStart: false,
        isEnd: false,
        metadata: { isEntryNode: true },
        icon: '🚀',
        description: 'Flow entry point',
        // Add individual transitions for entry node
        individualTransitions: [{
          type: 'success',
          to: this.startAction,
          label: 'Start',
          handleId: 'success-output'
        }]
      },
      dragHandle: '.node-drag-handle',
      className: 'contact-flow-node system entry'
    };
    nodes.push(entryNode);

    // Create regular nodes
    actions.forEach(action => {
      nodes.push(this.createNode(action));
    });

    return nodes;
  }

  // Override getAllTransitions to include entry transition
  getAllTransitions() {
    const allTransitions = [];
    
    // Add entry to start transition
    if (this.startAction) {
      allTransitions.push({
        type: 'success',
        from: 'entry-node',
        to: this.startAction,
        label: 'Start',
        priority: 1,
        transitionIndex: 0,
        handleId: 'success-output'
      });
    }
    
    // Add regular transitions
    this.getActions().forEach(action => {
      const transitions = this.getTransitions(action.Identifier);
      allTransitions.push(...transitions);
    });
    
    return allTransitions;
  }

  // Create a React Flow node from Contact Flow action
  createNode(action) {
    const position = this.getActionPosition(action.Identifier) || { x: 0, y: 0 };
    const nodeType = this.getNodeType(action);
    const category = this.getBlockCategory(action);

    // Compute display info once and reuse for the label (avoids a second pass
    // through getDisplayInfo + its lookups for every node).
    const displayInfo = this.getDisplayInfo(action);

    // Get all individual transitions for this action
    const individualTransitions = this.getIndividualTransitions(action);

    return {
      id: action.Identifier,
      type: nodeType,
      position,
      data: {
        label: this.getActionLabel(action, displayInfo),
        actionType: action.Type,
        category,
        parameters: this.getBlockParameters(action),
        isStart: this.isStartAction(action.Identifier),
        isEnd: this.isEndAction(action.Identifier),
        metadata: this.getActionMetadata(action),
        icon: this.getActionIcon(action),
        description: this.getActionDescription(action),
        // Add display details for specific node types
        displayInfo,
        // Add layout direction for handle positioning
        layoutDirection: this.layoutDirection,
        // Add individual transitions
        individualTransitions
      },
      dragHandle: '.node-drag-handle',
      className: `contact-flow-node ${category} ${action.Type.toLowerCase()}`,
    };
  }

  // Get individual transitions for a node.
  // Derived from the canonical getTransitions() builder so node handles and the
  // edges that connect to them can never diverge (handle ids/ordering are shared).
  getIndividualTransitions(action) {
    return this.getTransitions(action.Identifier).map(t => {
      const handle = { type: t.type, to: t.to, label: t.label, handleId: t.handleId };
      if (t.errorType) handle.errorType = t.errorType;
      if (t.condition) handle.condition = t.condition;
      return handle;
    });
  }

  // Get display information for specific node types
  getDisplayInfo(action) {
    const info = {};
    
    switch (action.Type) {
      case 'Compare':
        info.comparisonValue = action.Parameters?.ComparisonValue || '';
        info.conditions = this.getCompareConditions(action);
        break;
      case 'MessageParticipant':
        if (action.Parameters?.Text) {
          info.fullMessage = action.Parameters.Text;
          info.displayMessage = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        } else if (action.Parameters?.Media?.Uri) {
          info.mediaUri = action.Parameters.Media.Uri;
          info.contentType = 'media';
        }
        break;
      case 'GetParticipantInput':
        if (action.Parameters?.Text) {
          info.fullPrompt = action.Parameters.Text;
          info.displayPrompt = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        } else if (action.Parameters?.Media?.Uri) {
          info.mediaUri = action.Parameters.Media.Uri;
          info.contentType = 'media';
        }
        
        info.timeout = action.Parameters?.InputTimeLimitSeconds || 5;
        info.storeInput = action.Parameters?.StoreInput === 'True' || action.Parameters?.StoreInput === true;
        break;
      case 'ConnectParticipantWithLexBot':
        // Handle prompt display similar to GetParticipantInput
        if (action.Parameters?.Text) {
          info.fullPrompt = action.Parameters.Text;
          info.displayPrompt = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        }
        
        // Handle Lex Bot information
        if (action.Parameters?.LexV2Bot?.AliasArn) {
          info.lexBotInfo = this.lookupLexV2BotName(action.Parameters.LexV2Bot.AliasArn);
          info.lexBotType = 'v2';
        } else if (action.Parameters?.LexBot?.Name && action.Parameters?.LexBot?.Alias) {
          // Lex V1 is no longer exported, but legacy flows already on the instance can
          // still carry V1 LexBot references in their Content. This display-only branch
          // is intentionally kept so those older flows still render a meaningful label.
          info.lexBotInfo = {
            name: action.Parameters.LexBot.Name,
            alias: action.Parameters.LexBot.Alias,
            region: action.Parameters.LexBot.Region || 'Unknown'
          };
          info.lexBotType = 'v1';
        }
        
        info.timeout = action.Parameters?.InputTimeLimitSeconds || 5;
        break;
      case 'UpdateContactTextToSpeechVoice':
        if (action.Parameters?.TextToSpeechVoice) {
          info.textToSpeechVoice = action.Parameters.TextToSpeechVoice;
        }
        if (action.Parameters?.TextToSpeechEngine) {
          info.textToSpeechEngine = action.Parameters.TextToSpeechEngine;
        }
        if (action.Parameters?.TextToSpeechStyle) {
          info.textToSpeechStyle = action.Parameters.TextToSpeechStyle;
        }
        break;
      case 'TransferToFlow':
        info.targetFlowName = this.lookupFlowName(action.Parameters?.ContactFlowId);
        info.targetFlowId = action.Parameters?.ContactFlowId;
        break;
      case 'UpdateContactTargetQueue':
        // Handle both QueueId and AgentId parameters
        const targetId = action.Parameters?.QueueId || action.Parameters?.AgentId;
        
        if (targetId) {
          // Check if it's a JSONPath expression
          if (targetId.startsWith('$.')) {
            info.queueName = targetId; // Display JSONPath as-is
            info.isJsonPath = true;
          } else if (action.Parameters?.AgentId || (targetId && targetId.includes('/agent/'))) {
            // It's an agent target
            info.queueName = this.lookupUserName(targetId);
            info.isAgentTarget = true;
          } else {
            // Regular queue lookup
            info.queueName = this.lookupQueueName(targetId);
            info.isAgentTarget = false;
          }
        } else {
          info.queueName = 'Unknown Queue';
        }
        
        info.queueId = action.Parameters?.QueueId;
        info.agentId = action.Parameters?.AgentId;
        break;
      case 'CheckHoursOfOperation':
        if (action.Parameters?.HoursOfOperationId) {
          info.hoursName = this.lookupHoursName(action.Parameters.HoursOfOperationId);
          info.hoursId = action.Parameters.HoursOfOperationId;
        } else {
          info.hoursName = 'Current Queue';
        }
        break;
      case 'CheckMetricData':
        // Extract metric type information
        if (action.Parameters?.MetricType) {
          info.metricType = action.Parameters.MetricType;
        }
        
        // Handle Queue ID if present
        if (action.Parameters?.QueueId) {
          info.queueName = this.lookupQueueName(action.Parameters.QueueId);
          info.queueId = action.Parameters.QueueId;
        }
        
        // Handle Agent ID if present
        if (action.Parameters?.AgentId) {
          info.agentName = this.lookupUserName(action.Parameters.AgentId);
          info.agentId = action.Parameters.AgentId;
        }
        
        // Other metric parameters for display
        if (action.Parameters?.Statistic) {
          info.statistic = action.Parameters.Statistic;
        }
        if (action.Parameters?.Threshold) {
          info.threshold = action.Parameters.Threshold;
        }
        if (action.Parameters?.Operator) {
          info.operator = action.Parameters.Operator;
        }
        break;
      case 'TransferParticipantToThirdParty':
        info.phoneNumber = action.Parameters?.ThirdPartyPhoneNumber || '';
        info.timeout = action.Parameters?.ThirdPartyConnectionTimeLimitSeconds || 30;
        break;
      case 'InvokeLambdaFunction':
        info.functionName = this.extractLambdaName(action.Parameters?.LambdaFunctionARN);
        info.functionArn = action.Parameters?.LambdaFunctionARN;
        info.timeout = action.Parameters?.InvocationTimeLimitSeconds || 8;
        
        // Add Lambda invocation attributes if they exist
        if (action.Parameters?.LambdaInvocationAttributes) {
          info.lambdaInvocationAttributes = action.Parameters.LambdaInvocationAttributes;
          info.attributeCount = Object.keys(action.Parameters.LambdaInvocationAttributes).length;
        }
        break;
      case 'UpdateContactAttributes':
        info.attributeCount = action.Parameters?.Attributes ? Object.keys(action.Parameters.Attributes).length : 0;
        info.attributes = action.Parameters?.Attributes || {};
        break;
      case 'UpdateContactRecordingBehavior':
        info.recordingParticipants = action.Parameters?.RecordingBehavior?.RecordedParticipants || [];
        break;
      case 'CreateCallbackContact':
        if (action.Parameters?.QueueId) {
        info.targetQueue = this.lookupQueueName(action.Parameters?.QueueId);
        } else {
          info.targetQueue = 'Current Queue';
        }
        info.initialDelay = action.Parameters?.InitialCallDelaySeconds || 0;
        info.maxAttempts = action.Parameters?.MaximumConnectionAttempts || 1;
        break;
      case 'UpdateContactEventHooks':
        if (action.Parameters?.EventHooks) {
          const eventHooks = action.Parameters.EventHooks;
          const hookType = Object.keys(eventHooks)[0]; // Get the first (and likely only) hook type
          const hookValue = eventHooks[hookType];
          
          info.eventHookType = hookType;
          info.eventHookValue = hookValue;
          
          // Look up names based on hook type and ARN type
          if (['AgentHold', 'AgentWhisper', 'CustomerHold', 'CustomerRemaining', 'CustomerWhisper'].includes(hookType)) {
            // These use flow ARNs
            info.eventHookTargetName = this.lookupFlowName(hookValue);
          } else if (hookType === 'CustomerQueue') {
            // CustomerQueue can use either flow ARN or queue ARN - check which one
            if (hookValue && hookValue.includes('/queue/')) {
              info.eventHookTargetName = this.lookupQueueName(hookValue);
            } else if (hookValue && hookValue.includes('/contact-flow/')) {
              info.eventHookTargetName = this.lookupFlowName(hookValue);
            }
          }
          // For other hook types (DefaultAgentUI, DisconnectAgentUI, PauseContact, ResumeContact),
          // we don't need to look up names as they don't reference external resources
        }
        break;
      case 'UpdateContactRoutingBehavior':
        if (action.Parameters?.QueuePriority !== undefined) {
          info.queuePriority = action.Parameters.QueuePriority;
          info.routingType = 'priority';
        } else if (action.Parameters?.QueueTimeAdjustmentSeconds !== undefined) {
          info.queueTimeAdjustment = action.Parameters.QueueTimeAdjustmentSeconds;
          info.routingType = 'timeAdjustment';
        }
        break;

      case 'ShowView':
        if (action.Parameters?.ViewResource?.Id) {
          info.viewId = action.Parameters.ViewResource.Id;
          info.viewVersion = action.Parameters.ViewResource.Version;
        }
        info.timeout = action.Parameters?.InvocationTimeLimitSeconds || 400;
        break;

      case 'Wait':
        info.timeoutSeconds = action.Parameters?.TimeoutSeconds || 30;
        break;

      case 'Loop':
        info.loopCount = action.Parameters?.LoopCount || 1;
        break;

      case 'MessageParticipantIteratively':
        if (action.Parameters?.Messages && Array.isArray(action.Parameters.Messages)) {
          info.messages = action.Parameters.Messages.map((message, index) => {
            let content = '';
            let contentType = 'text';
            
            if (message.Text) {
              content = message.Text;
              contentType = 'text';
            } else if (message.SSML) {
              content = message.SSML;
              contentType = 'ssml';
            } else if (message.PromptId) {
              content = this.lookupPromptName(message.PromptId);
              contentType = 'prompt';
            } else if (message.Media?.Uri) {
              content = message.Media.Uri;
              contentType = 'media';
            }
            
            return {
              index: index + 1,
              content: this.truncateText(content, 100),
              fullContent: content,
              contentType
            };
          });
          info.messageCount = info.messages.length;
        }
        break;
      case 'UpdatePreviousContactParticipantState':
        if (action.Parameters?.PreviousContactParticipantState) {
          const state = action.Parameters.PreviousContactParticipantState;
          info.participantState = state;
          
          // Map the state to display text
          const stateDisplayMap = {
            'AgentOnHold': 'Hold Agent',
            'CustomerOnHold': 'Hold Customer', 
            'OffHold': 'Conference All'
          };
          
          info.displayState = stateDisplayMap[state] || state;
        }
        break;
      case 'InvokeFlowModule':
        info.flowModuleName = this.lookupFlowModuleName(action.Parameters?.FlowModuleId);
        info.flowModuleId = action.Parameters?.FlowModuleId;
        break;
      case 'CompleteOutboundCall':
        if (action.Parameters?.CallerId?.Number) {
          info.phoneNumber = action.Parameters.CallerId.Number;
        }
        break;
      default:
        return info;
    }
    
    return info;
  }

  // Get conditions for Compare nodes with better formatting
  getCompareConditions(action) {
    if (!action.Transitions?.Conditions) return [];
    
    return action.Transitions.Conditions.map((condition, index) => ({
      label: this.getConditionLabel(condition, index),
      operator: condition.Condition?.Operator || '',
      operands: condition.Condition?.Operands || [],
      nextAction: condition.NextAction
    }));
  }

  // Condition label for Compare nodes and CheckHoursOfOperation
  getConditionLabel(condition, index, actionType = null) {
    if (!condition.Condition) return `Branch ${index + 1}`;
    
    const { Operator, Operands } = condition.Condition;
    
    // Special handling for CheckHoursOfOperation
    if (actionType === 'CheckHoursOfOperation' && Operator === 'Equals' && Operands && Operands.length > 0) {
      if (Operands[0] === 'True') {
        return 'In Hours';
      }
      if (Operands[0] === 'False') {
        return 'Out of Hours';
      }
    }

    if (actionType === 'GetParticipantInput' && Operator === 'Equals' && Operands && Operands.length > 0) {
      const input = Operands[0];
      // If it's a number input, show it clearly
      if (/^\d+$/.test(input)) {
        return `Press ${input}`;
      }
      return `"${input}"`;
    }
    
    if (Operator === 'Equals' && Operands && Operands.length > 0) {
      return `= "${Operands[0]}"`;
    }
    
    if (Operator === 'NumberLessThan' && Operands && Operands.length > 0) {
      return `< ${Operands[0]}`;
    }
    
    if (Operator === 'NumberGreaterThan' && Operands && Operands.length > 0) {
      return `> ${Operands[0]}`;
    }
    
    if (Operator === 'NumberEqualTo' && Operands && Operands.length > 0) {
      return `= ${Operands[0]}`;
    }
    
    if (Operands && Operands.length > 0) {
      return `${Operator}: ${Operands.join(', ')}`;
    }
    
    return Operator || `Branch ${index + 1}`;
  }

  // Create a React Flow edge from Contact Flow transition
  createEdge(transition, globalIndex = 0, nodes = []) {
    const edgeType = this.getEdgeType(transition);
    
    // Create unique ID
    let uniqueId = `${transition.from}-${transition.to}-${transition.type}`;
    
    if (transition.transitionIndex !== undefined) {
      uniqueId += `-t${transition.transitionIndex}`;
    }
    
    uniqueId += `-g${globalIndex}`;
    
    if (transition.errorType) {
      uniqueId += `-${transition.errorType.replace(/[^a-zA-Z0-9]/g, '')}`;
    }
    
    if (transition.condition && transition.condition.Operands) {
      const operandStr = transition.condition.Operands.join('').replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
      if (operandStr) {
        uniqueId += `-${operandStr}`;
      }
    }
    
    // Use the specific handle ID from the transition
    const sourceHandle = transition.handleId || `${transition.type}-output`;
    
    return {
      id: uniqueId,
      source: transition.from,
      target: transition.to,
      sourceHandle: sourceHandle,
      targetHandle: null, // Use default target handle
      type: edgeType,
      label: '', // No label on edges since we'll show it on handles
      data: {
        label: '', // Keep empty since label will be on handle
        transitionType: transition.type,
        condition: transition.condition,
        errorType: transition.errorType,
        actualHandle: sourceHandle
      },
      style: {
        stroke: this.getEdgeColor(transition.type),
        strokeWidth: 2,
        strokeDasharray: transition.type === 'error' ? '5,5' : undefined
      },
      markerEnd: {
        type: 'arrowclosed',
        color: this.getEdgeColor(transition.type),
      },
      labelBgStyle: {
        fill: '#ffffff',
        fillOpacity: 0.8
      }
    };
  }

  // Transitions with better condition labels
  getTransitions(actionId) {
    const action = this.getAction(actionId);
    if (!action || !action.Transitions) return [];

    const transitions = [];
    let handleIndex = 0;

    // Special handling for GetParticipantInput with StoreInput
    if (action.Type === 'GetParticipantInput') {
      const storeInput = action.Parameters?.StoreInput === 'True' || action.Parameters?.StoreInput === true;
      
      if (storeInput && action.Transitions.NextAction) {
        // If StoreInput is true, add success transition
        transitions.push({
          type: 'success',
          from: actionId,
          to: action.Transitions.NextAction,
          label: 'Success',
          priority: 1,
          transitionIndex: handleIndex,
          actionType: action.Type,
          handleId: `success-output-${handleIndex++}`
        });
      }
      
      // Add error transitions
      if (action.Transitions.Errors) {
        action.Transitions.Errors.forEach((error, index) => {
          transitions.push({
            type: 'error',
            from: actionId,
            to: error.NextAction,
            label: this.getErrorLabel(error.ErrorType),
            errorType: error.ErrorType,
            priority: 3,
            transitionIndex: handleIndex,
            actionType: action.Type,
            handleId: `error-output-${handleIndex++}`
          });
        });
      }

      // Add condition transitions
      if (action.Transitions.Conditions) {
        action.Transitions.Conditions.forEach((condition, index) => {
          const conditionLabel = this.getConditionLabel(condition, index, action.Type);
          transitions.push({
            type: 'condition',
            from: actionId,
            to: condition.NextAction,
            label: conditionLabel,
            condition: condition.Condition,
            priority: 2,
            transitionIndex: handleIndex,
            actionType: action.Type,
            handleId: `condition-output-${handleIndex++}`
          });
        });
      }
      
      return transitions;
    }

    if (action.Type === 'ConnectParticipantWithLexBot') {
    // ConnectParticipantWithLexBot never has success transitions
    
    // Add error transitions
    if (action.Transitions.Errors) {
      action.Transitions.Errors.forEach((error, index) => {
        transitions.push({
          type: 'error',
          from: actionId,
          to: error.NextAction,
          label: this.getErrorLabel(error.ErrorType),
          errorType: error.ErrorType,
          priority: 3,
          transitionIndex: handleIndex,
          actionType: action.Type,
          handleId: `error-output-${handleIndex++}`
        });
      });
    }

    // Add condition transitions
    if (action.Transitions.Conditions) {
      action.Transitions.Conditions.forEach((condition, index) => {
        const conditionLabel = this.getConditionLabel(condition, index, action.Type);
        transitions.push({
          type: 'condition',
          from: actionId,
          to: condition.NextAction,
          label: conditionLabel,
          condition: condition.Condition,
          priority: 2,
          transitionIndex: handleIndex,
          actionType: action.Type,
          handleId: `condition-output-${handleIndex++}`
        });
      });
    }
    
    return transitions;
  }

    // Add next action transition only for nodes that should have it
    if (action.Transitions.NextAction && !NO_SUCCESS_TRANSITION_TYPES.includes(action.Type)) {
      transitions.push({
        type: 'success',
        from: actionId,
        to: action.Transitions.NextAction,
        label: 'Success',
        priority: 1,
        transitionIndex: handleIndex,
        actionType: action.Type,
        handleId: `success-output-${handleIndex++}`
      });
    }

    // Add error transitions
    if (action.Transitions.Errors) {
      action.Transitions.Errors.forEach((error, index) => {
        transitions.push({
          type: 'error',
          from: actionId,
          to: error.NextAction,
          label: this.getErrorLabel(error.ErrorType),
          errorType: error.ErrorType,
          priority: 3,
          transitionIndex: handleIndex,
          actionType: action.Type,
          handleId: `error-output-${handleIndex++}`
        });
      });
    }

    // Add condition transitions with labels
    if (action.Transitions.Conditions) {
      action.Transitions.Conditions.forEach((condition, index) => {
        const conditionLabel = this.getConditionLabel(condition, index, action.Type);
        transitions.push({
          type: 'condition',
          from: actionId,
          to: condition.NextAction,
          label: conditionLabel,
          condition: condition.Condition,
          priority: 2,
          transitionIndex: handleIndex,
          actionType: action.Type,
          handleId: `condition-output-${handleIndex++}`
        });
      });
    }

    return transitions;
  }

  // Rest of the methods remain the same...
  getActionPosition(actionId) {
    const metadata = this.metadata[actionId];
    if (metadata && metadata.position) {
      return {
        x: metadata.position.x || 0,
        y: metadata.position.y || 0
      };
    }
    return null;
  }

  getActions() {
    return Array.from(this.actions.values());
  }

  getAction(identifier) {
    return this.actions.get(identifier);
  }

  getEdgeType(transition) {
    // Use advanced edge types for better routing
    switch (transition.type) {
      case 'error':
        return 'advancedErrorEdge';
      case 'condition':
        return 'advancedConditionEdge';
      default:
        return 'advancedEdge';
    }
  }

  getEdgeColor(transitionType) {
    const colors = {
      success: '#4CAF50',
      error: '#F44336',
      condition: '#2196F3',
      default: '#666666'
    };
    return colors[transitionType] || colors.default;
  }

  isStartAction(actionId) {
    return false;
  }

  isEndAction(actionId) {
    const action = this.getAction(actionId);
    if (!action) return false;
    
    return action.Type === 'DisconnectParticipant' || 
           action.Type === 'EndFlowExecution' ||
           !action.Transitions ||
           Object.keys(action.Transitions).length === 0;
  }

  getActionIcon(action) {
    const icons = {
      'MessageParticipant': '💬',
      'GetParticipantInput': '⌨️',
      'InvokeLambdaFunction': '🔗',
      'UpdateContactTargetQueue': '🚥',
      'TransferContactToQueue': '📞',
      'TransferToFlow': '🔄',
      'CheckHoursOfOperation': '🕐',
      'CheckMetricData': '📊',
      'DisconnectParticipant': '📵',
      'UpdateContactRecordingBehavior': '🎙️',
      'UpdateContactAttributes': '📊',
      'TransferParticipantToThirdParty': '📞',
      'Compare': '⚖️',
      'CreateCallbackContact': '📲',
      'UpdateContactCallbackNumber': '📱',
      'UpdateContactRoutingBehavior': '🔀',
      'ShowView': '👁️',
      'Wait': '⏳',
      'MessageParticipantIteratively': '🔁',
      'Loop': '🔁',
      'InvokeFlowModule': '📁',
      'CompleteOutboundCall': '📞'
    };
    
    return icons[action.Type] || '⚙️';
  }

  getActionDescription(action) {
    const descriptions = {
      'MessageParticipant': 'Plays a message to the participant',
      'GetParticipantInput': 'Collects input from the participant',
      'InvokeLambdaFunction': 'Invokes an AWS Lambda function',
      'UpdateContactTargetQueue': 'Sets the queue for the contact',
      'TransferContactToQueue': 'Transfers the contact to a queue',
      'TransferToFlow': 'Transfers to another contact flow',
      'CheckHoursOfOperation': 'Checks if within operating hours',
      'CheckMetricData': 'Checks metric data and branches based on conditions',
      'DisconnectParticipant': 'Ends the contact',
      'UpdateContactRecordingBehavior': 'Controls call recording',
      'UpdateContactAttributes': 'Sets contact attributes',
      'TransferParticipantToThirdParty': 'Transfers to external number',
      'Compare': 'Compares a value and branches based on conditions',
      'UpdateContactRoutingBehavior': 'Updates contact routing priority or time adjustment',
      'ShowView': 'Displays a view to the participant',
      'Wait': 'Waits for a specified duration',
      'MessageParticipantIteratively': 'Plays multiple messages in a loop',
      'Loop': 'Loop Number of Times'
    };
    
    return descriptions[action.Type] || `Performs ${action.Type} action`;
  }

  getBlockCategory(action) {
    const categoryMap = {
      'MessageParticipant': 'communication',
      'MessageParticipantIteratively': 'communication',
      'GetParticipantInput': 'input',
      'InvokeLambdaFunction': 'integration',
      'ConnectParticipantWithLexBot': 'input',
      'UpdateContactTargetQueue': 'routing',
      'TransferContactToQueue': 'routing',
      'TransferToFlow': 'routing',
      'UpdateContactRoutingBehavior': 'routing',
      'SetWorkingQueue': 'routing',
      'ChangeRoutingPriority': 'routing',
      'CheckHoursOfOperation': 'validation',
      'CheckMetricData': 'validation',
      'Compare': 'validation',
      'DisconnectParticipant': 'terminator',
      'EndFlowExecution': 'terminator',
      'UpdateContactRecordingBehavior': 'recording',
      'UpdateContactAttributes': 'data',
      'GetAttributes': 'data',
      'StoreCustomerInput': 'data',
      'TransferParticipantToThirdParty': 'transfer',
      'CreateCallbackContact': 'callback',
      'UpdateContactCallbackNumber': 'callback',
      'UpdateContactTextToSpeechVoice': 'communication',
      'UpdatePreviousContactParticipantState': 'communication',
      'ShowView': 'communication',
      'Hold': 'communication',
      'Resume': 'communication',
      'UpdateContactEventHooks': 'system',
      'UpdateFlowLoggingBehavior': 'system',
      'Wait': 'system',
      'WaitForEvent': 'system',
      'Loop': 'system'
    };
    
    return categoryMap[action.Type] || 'general';
  }

  getBlockParameters(action) {
    if (!action.Parameters) return {};

    switch (action.Type) {
      case 'MessageParticipant':
        return {
          text: action.Parameters.Text || '',
          textType: action.Parameters.TextType || 'text'
        };
      case 'GetParticipantInput':
        return {
          text: action.Parameters.Text || '',
          timeout: action.Parameters.InputTimeLimitSeconds || 5,
          storeInput: action.Parameters.StoreInput || false
        };
      case 'InvokeLambdaFunction':
        return {
          functionArn: action.Parameters.LambdaFunctionARN || '',
          timeout: action.Parameters.InvocationTimeLimitSeconds || 8
        };
      case 'Compare':
        return {
          comparisonValue: action.Parameters.ComparisonValue || ''
        };
      default:
        return action.Parameters;
    }
  }

  getActionMetadata(action) {
    return {
      identifier: action.Identifier,
      type: action.Type,
      hasErrors: !!(action.Transitions?.Errors?.length),
      hasConditions: !!(action.Transitions?.Conditions?.length),
      errorCount: action.Transitions?.Errors?.length || 0,
      conditionCount: action.Transitions?.Conditions?.length || 0,
      parameters: this.getBlockParameters(action)
    };
  }

  getErrorLabel(errorType) {
    const errorLabels = {
      'NoMatchingError': 'Error',
      'NoMatchingCondition': 'No Match',
      'NoMatch': 'No Match',
      'CallbackNotDialable': 'Invalid Number',
      'InvalidPhoneNumber': 'Invalid Number',
      'InvalidEmailAddress': 'Invalid Email',
      'InvalidContactFlow': 'Invalid Flow',
      'InvalidLambdaResponse': 'Lambda Error',
      'RequestTimedOut': 'Timeout',
      'Timeout': 'Timeout',
      'ExceptionThrown': 'Exception',
      'Error': 'Error',
      'InputTimeLimitExceeded': 'Timeout'
    };
    return errorLabels[errorType] || errorType;
  }

  // The instance slice of exportedData, or null when lookups can't be resolved.
  _instanceData() {
    return (this.exportedData && this.instanceId) ? this.exportedData[this.instanceId] : null;
  }

  // Memoized id -> object map for a resource collection on the current instance.
  // Returns null when the collection is absent (callers fall back to the raw id),
  // preserving the original lookup semantics while replacing O(n) Array.find scans.
  _lookupMap(collectionKey, idKey = 'Id') {
    const data = this._instanceData();
    const arr = data ? data[collectionKey] : null;
    if (!Array.isArray(arr)) return null;
    if (!this._maps) this._maps = {};
    if (this._maps[collectionKey]) return this._maps[collectionKey];
    const map = new Map();
    arr.forEach(item => { if (item && item[idKey] != null) map.set(item[idKey], item); });
    this._maps[collectionKey] = map;
    return map;
  }

  lookupFlowName(flowArn) {
    if (!flowArn) return 'Unknown Flow';
    const flowId = this.extractIdFromArn(flowArn);
    const map = this._lookupMap('contactFlows');
    if (!map) return flowId;
    return map.get(flowId)?.Name || `Flow: ${flowId}`;
  }

  lookupQueueName(queueArn) {
    if (!queueArn) return 'Unknown Queue';
    const queueId = this.extractIdFromArn(queueArn);
    const map = this._lookupMap('queues');
    if (!map) return queueId;
    return map.get(queueId)?.Name || `Queue: ${queueId}`;
  }

  lookupHoursName(hoursArn) {
    if (!hoursArn) return 'Current Queue';
    const hoursId = this.extractIdFromArn(hoursArn);
    const map = this._lookupMap('hoursOfOperation');
    if (!map) return hoursId;
    return map.get(hoursId)?.Name || `Hours: ${hoursId}`;
  }

  lookupPromptName(promptArn) {
    if (!promptArn) return 'Unknown Prompt';
    const promptId = this.extractIdFromArn(promptArn);
    const map = this._lookupMap('prompts');
    if (!map) return promptId;
    return map.get(promptId)?.Name || `Prompt: ${promptId}`;
  }

  lookupUserName(userArn) {
    if (!userArn) return 'Unknown User';
    const userId = this.extractIdFromArn(userArn);
    const map = this._lookupMap('users');
    if (!map) return userId;
    return map.get(userId)?.Username || `Agent: ${userId}`;
  }

  lookupFlowModuleName(flowModuleId) {
    if (!flowModuleId) return 'Unknown Module';

    // Check if it's a JSONPath expression
    if (flowModuleId.startsWith('$.')) {
      return flowModuleId; // Display JSONPath as-is
    }

    const moduleId = this.extractIdFromArn(flowModuleId);
    const map = this._lookupMap('contactFlowModules');
    if (!map) return moduleId;
    return map.get(moduleId)?.Name || `Module: ${moduleId}`;
  }


  lookupLexV2BotName(botAliasArn) {
    if (!botAliasArn) return { name: 'Unknown Bot', alias: 'Unknown' };

    // Check if it's a JSONPath expression
    if (botAliasArn.startsWith('$.')) {
      return { name: botAliasArn, alias: 'Dynamic' };
    }

    const map = this._lookupMap('lexBotV2', 'botId');
    if (!map) return this.extractLexV2InfoFromArn(botAliasArn);

    // Extract bot ID and alias ID from ARN
    // ARN format: arn:aws:lex:region:account:bot-alias/BOT_ID/ALIAS_ID
    const arnParts = botAliasArn.split('/');
    if (arnParts.length < 3) {
      return this.extractLexV2InfoFromArn(botAliasArn);
    }

    const botId = arnParts[arnParts.length - 2];
    const aliasId = arnParts[arnParts.length - 1];

    const lexBot = map.get(botId);
    if (lexBot) {
      const botAlias = lexBot.botAliases?.find(alias => alias.botAliasId === aliasId);
      return {
        name: lexBot.botName || 'Unknown Bot',
        alias: botAlias?.botAliasName || aliasId || 'Unknown Alias',
        botId: botId,
        aliasId: aliasId
      };
    }
    return {
      name: `Bot: ${botId}`,
      alias: `Alias: ${aliasId}`,
      botId: botId,
      aliasId: aliasId
    };
  }

  extractLexV2InfoFromArn(botAliasArn) {
    if (!botAliasArn) return { name: 'Unknown Bot', alias: 'Unknown' };
    
    const arnParts = botAliasArn.split('/');
    if (arnParts.length >= 3) {
      const botId = arnParts[arnParts.length - 2];
      const aliasId = arnParts[arnParts.length - 1];
      return {
        name: `Bot: ${botId}`,
        alias: `Alias: ${aliasId}`,
        botId: botId,
        aliasId: aliasId
      };
    }
    
    return { name: 'Unknown Bot', alias: 'Unknown' };
  }

  extractIdFromArn(arn) {
    if (!arn) return 'Unknown';
    const parts = arn.split('/');
    return parts[parts.length - 1] || arn;
  }

  extractLambdaName(functionArn) {
    if (!functionArn) return 'Unknown';
    const parts = functionArn.split(':');
    return parts[parts.length - 1] || 'Lambda';
  }

  formatActionType(type) {
    return type.replace(/([A-Z])/g, ' $1').trim();
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  getActionLabel(action, displayInfo = this.getDisplayInfo(action)) {
    switch (action.Type) {
      case 'Compare':
        return `Compare: ${displayInfo.comparisonValue || 'Value'}`;
      
      case 'MessageParticipant':
        return 'Play Message';
      
      case 'GetParticipantInput':
        return 'Get Input';

      case 'ConnectParticipantWithLexBot':
        if (displayInfo.lexBotInfo) {
          if (displayInfo.lexBotType === 'v2') {
            return `Lex Bot: ${displayInfo.lexBotInfo.name || 'Unknown'} (${displayInfo.lexBotInfo.alias || 'Unknown'})`;
          } else {
            return `Lex Bot: ${displayInfo.lexBotInfo.name} (${displayInfo.lexBotInfo.alias})`;
          }
        }
        return 'Connect to Lex Bot';
      
      case 'TransferToFlow':
        return displayInfo.targetFlowName || 'Transfer to Flow';
      
      case 'UpdateContactTargetQueue':
        return displayInfo.queueName || 'Set Queue';
      
      case 'CheckHoursOfOperation':
        return displayInfo.hoursName || 'Check Hours';
      
      case 'CheckMetricData':
        if (displayInfo.metricType) {
          return `Check ${displayInfo.metricType}`;
        }
        return 'Check Metrics';
      
      case 'TransferParticipantToThirdParty':
        return `External Transfer: ${displayInfo.phoneNumber || 'Phone'}`;
      
      case 'InvokeLambdaFunction':
        return `Lambda: ${displayInfo.functionName || 'Function'}`;
      
      case 'UpdateContactAttributes':
        return `Set ${displayInfo.attributeCount} Attribute${displayInfo.attributeCount !== 1 ? 's' : ''}`;
      
      case 'UpdateContactRecordingBehavior':
        const participants = displayInfo.recordingParticipants;
        if (participants.length === 0) {
          return 'Stop Recording';
        } else if (participants.includes('Agent') && participants.includes('Customer')) {
          return 'Record All';
        } else if (participants.includes('Agent')) {
          return 'Record Agent';
        } else if (participants.includes('Customer')) {
          return 'Record Customer';
        }
        return 'Update Recording';
      
      case 'CreateCallbackContact':
        return `Callback: ${displayInfo.targetQueue || 'Queue'}`;
      case 'UpdateContactEventHooks':
        if (displayInfo.eventHookType) {
          const hookType = displayInfo.eventHookType;
          const hookLabels = {
            'AgentHold': 'Set Agent Hold Flow',
            'AgentWhisper': 'Set Agent Whisper Flow',
            'CustomerHold': 'Set Customer Hold Flow',
            'CustomerQueue': 'Set Customer Queue Flow',
            'CustomerRemaining': 'Set Disconnect Flow',
            'CustomerWhisper': 'Set Customer Whisper Flow',
            'DefaultAgentUI': 'Default flow for Agent UI',
            'DisconnectAgentUI': 'Disconnect flow for Agent UI',
            'PauseContact': 'Flow at Contact Pause',
            'ResumeContact': 'Flow at Contact Resume'
          };
          
          const baseLabel = hookLabels[hookType] || 'Set Event Hooks';
          
          // Add the target name if available and relevant
          if (displayInfo.eventHookTargetName && 
              ['AgentHold', 'AgentWhisper', 'CustomerHold', 'CustomerRemaining', 'CustomerWhisper', 'CustomerQueue'].includes(hookType)) {
            return `${baseLabel}: ${displayInfo.eventHookTargetName}`;
          } else {
            return baseLabel;
          }
        }
        return 'Set Event Hooks'; 
      case 'UpdateContactRoutingBehavior':
        if (displayInfo.routingType === 'priority') {
          return `Set Priority: ${displayInfo.queuePriority}`;
        } else if (displayInfo.routingType === 'timeAdjustment') {
          return `Adjust Time: ${displayInfo.queueTimeAdjustment}s`;
        }
        return 'Update Routing';

      case 'ShowView':
        return displayInfo.viewId ? `Show View: ${displayInfo.viewId}` : 'Show View';

      case 'Wait':
        return `Wait: ${displayInfo.timeoutSeconds}s`;

      case 'Loop':
        return `Loop (${displayInfo.loopCount}x)`;

      case 'MessageParticipantIteratively':
        return displayInfo.messageCount > 0 ? 
          `Loop Messages (${displayInfo.messageCount})` : 
          'Loop Messages';

      case 'UpdatePreviousContactParticipantState':
        return displayInfo.displayState || 'Hold Customer or Agent';
      
      case 'InvokeFlowModule':
        return displayInfo.flowModuleName || 'Invoke Flow Module';
      
      case 'CompleteOutboundCall':
        return displayInfo.phoneNumber ? `Call: ${displayInfo.phoneNumber}` : 'Complete Outbound Call';
      
      default:
        return this.getOriginalActionLabel(action);
    }
  }

  getOriginalActionLabel(action) {
    const labels = {
      'UpdateFlowLoggingBehavior': 'Enable Logging',
      'DisconnectParticipant': 'Disconnect',
      'UpdateContactEventHooks': 'Set Event Hooks',
      'MessageParticipantIteratively': 'Loop Message',
      'EndFlowExecution': 'End Flow',
      'GetAttributes': 'Get Attributes',
      'WaitForEvent': 'Wait for Event',
      'SetWorkingQueue': 'Set Working Queue',
      'ChangeRoutingPriority': 'Change Priority',
      'Hold': 'Hold',
      'Resume': 'Resume',
      'ConnectParticipantWithLexBot': 'Lex Bot',
      'StoreCustomerInput': 'Store Input'
    };

    return labels[action.Type] || this.formatActionType(action.Type);
  }

  getNodeType(action) {
    const decisionTypes = ['CheckHoursOfOperation', 'Compare'];
    const endTypes = ['DisconnectParticipant', 'EndFlowExecution'];
    
    if (endTypes.includes(action.Type)) {
      return 'endNode';
    }
    
    if (decisionTypes.includes(action.Type)) {
      return 'decisionNode';
    }
    
    return 'processNode';
  }

  getFlowSummary() {
    const actions = this.getActions();
    const transitions = this.getAllTransitions();
    
    const blockTypes = {};
    actions.forEach(action => {
      blockTypes[action.Type] = (blockTypes[action.Type] || 0) + 1;
    });

    return {
      version: this.flow.Version,
      startAction: this.startAction,
      actionCount: actions.length,
      transitionCount: transitions.length,
      hasPositionData: Object.keys(this.metadata).length > 0,
      blockTypes
    };
  }
}